import type { DeliveryEntryBuildState } from '@/features/delivery/types/contracts';
import type {
  BuildScope,
  MetadataStatus,
  OutputBuildState,
} from '@/features/delivery/types/outputContracts';

/** Root TanStack Query key namespace for the customer Delivery feature. */
export const DELIVERY_QUERY_KEY = 'delivery' as const;

/**
 * Page size for the keyset-paginated delivery list (`GET /deliveries`,
 * `.manage`-gated). Within the backend clamp of [1, 500]; a moderate page keeps
 * the console table light while still paging quickly via the virtual range bridge.
 */
export const DELIVERIES_PAGE_SIZE = 50;

/**
 * Page size for a output's keyset-paginated entries
 * (`GET /deliveries/packages/:id/entries`), whether the listing is narrowed by
 * `?batchNo=` or not. Sized to cover a typical batch in one page while still paging
 * (via the virtual range bridge) for a larger one — a batch's entry count
 * is whatever the delivery's `batchMaxItems` is, so it is not bounded by anything this
 * page size can assume. Within the backend clamp of [1, 500].
 */
export const OUTPUT_ENTRIES_PAGE_SIZE = 100;

/**
 * Page size for a delivery's keyset-paginated entries (`GET /deliveries/:id/entries`),
 * used both for the whole-delivery listing and for the `?batchNo=`-narrowed one behind
 * the batch filter. Matches {@link OUTPUT_ENTRIES_PAGE_SIZE} so the two sides of the
 * entries workspace page at the same rate; within the backend clamp of [1, 500] (the
 * backend's own default is also 100).
 */
export const DELIVERY_ENTRIES_PAGE_SIZE = 100;

/**
 * Page size for the output's orphaned-output audit
 * (`GET /deliveries/packages/:id/outputs/orphans`). Deliberately smaller than the
 * entry listings: every row costs the backend a storage probe, and a page can come
 * back EMPTY with a non-null cursor (the pure re-check drops candidates the SQL scan
 * offered), so the audit is expected to walk several pages — a smaller page keeps each
 * round trip bounded rather than making the first one slow. Within the backend clamp
 * of [1, 500].
 */
export const OUTPUT_ORPHANS_PAGE_SIZE = 50;

/**
 * How many source-side orphan candidates to ask for
 * (`GET /deliveries/:id/entries/outputs/orphans?limit=`). That report has NO cursor, so
 * this is the whole coverage bound, not a page: a report that comes back at exactly
 * this length may have been truncated, which is why the panel says so instead of
 * reading as "all of them".
 */
export const SOURCE_ORPHANS_LIMIT = 200;

/**
 * How many object paths a purge confirmation lists in full before summarizing the rest
 * as "and N more". A destructive prompt has to SHOW what it will delete, but a
 * hundred-line description pushes its own confirm button off the screen — so the cap
 * exists and the remainder is always stated (see `lib/orphanAudit.ts`), never dropped.
 */
export const PURGE_PATH_PREVIEW_MAX = 8;

/**
 * The two views of the shown entries, in display order: the delivery's own source
 * entries and their mirrored output entries. This is a **local facet** of the
 * workspace, not navigation — deliberately not a search param, so switching it keeps
 * the batch filter, the header, and the scroll region in place instead of reading as a
 * page jump.
 */
export const ENTRIES_FACETS = ['source', 'output'] as const;
export type EntriesFacet = (typeof ENTRIES_FACETS)[number];

/**
 * The batch filter's "no batch filter" `<option>` value. `undefined` is what "all
 * batches" means everywhere else in the feature — an omitted `?batch=` and an omitted
 * `?batchNo=` — but a DOM `<option>` value has to be a string, so this is the one place
 * the absence needs a name. Deliberately not a number: it can never collide with a real
 * `batchNo`.
 */
export const ALL_BATCHES_VALUE = 'all' as const;

/**
 * The state filter's "no state filter" `<option>` value, for the same reason as
 * {@link ALL_BATCHES_VALUE}: `undefined` is what "every state" means on the wire (an
 * omitted `?state=`), but a DOM `<option>` value has to be a string. Safe to share
 * across both facets — it collides with no member of either vocabulary below.
 */
export const ALL_STATES_VALUE = 'all' as const;
export const ALL_METADATA_STATUSES_VALUE = 'all' as const;
export const OUTPUT_METADATA_STATUSES = [
  'ok',
  'warning',
  'incomplete',
  'not_evaluated',
] as const satisfies readonly MetadataStatus[];

/**
 * The two entry facets' `?state=` vocabularies, in the order the filter offers them —
 * roughly the order an operator walks a build: what hasn't started, what can't, what is
 * in flight, what is done, what broke.
 *
 * They belong to different endpoints. `SOURCE_ENTRY_BUILD_STATES` is the delivery
 * entry's current-generation build projection (`GET /deliveries/:id/entries`), while
 * `OUTPUT_ENTRY_STATES` is the output entry's persisted build state. Both
 * listings are fail-closed — a value from the wrong vocabulary is a `400`, never a wider
 * page — so a selection must be dropped when the facet switches rather than carried
 * over. Labels stay at the call site (`EntryStateFilterControl`), which reuses the same
 * wording as `EntryStateBadge`/`OutputStateBadge` so the filter and the rows it
 * produces name a state identically.
 */
export const SOURCE_ENTRY_BUILD_STATES = [
  'pending',
  'building',
  'ready',
  'failed',
  'deleted',
] as const satisfies readonly DeliveryEntryBuildState[];
export const OUTPUT_ENTRY_STATES = [
  'pending',
  'blocked',
  'building',
  'ready',
  'failed',
] as const satisfies readonly OutputBuildState[];

/**
 * Any value the state filter can hold — the union of the two vocabularies above, which
 * is what a single control over both facets selects. Narrowed back to one facet's own
 * vocabulary before it reaches a listing (see `lib/entriesView.ts`), never sent as this
 * union.
 */
export type EntryStateFilter =
  | (typeof SOURCE_ENTRY_BUILD_STATES)[number]
  | (typeof OUTPUT_ENTRY_STATES)[number];

/**
 * Every batch-level action id, in the order the batch's actions menu offers them.
 * Availability (whether the selected batch can invoke them at all, and which are
 * grouped last as destructive) is computed by `lib/batchActions.ts`; this is only the
 * vocabulary. Labels stay as Lingui `Trans`/`t` at the call sites, and each id's
 * leading icon is in `config/batchActionIcons.ts`.
 *
 * All three are the SAME endpoint (`POST .../batches/:batchNo/build`) under three
 * scopes — which is exactly why a batch is still a real action scope even though it is
 * no longer a navigable level. `add-entry` and `sync-membership` used to be in this
 * list; both left when the workspace flattened, because neither was ever batch-scoped:
 * adding an entry posts to the delivery (the server picks the batch) and reconciling
 * membership is a whole-output `PUT`. They sit at their real scopes now — the entries
 * toolbar and `OutputActionsMenu`.
 */
const BATCH_ACTIONS = [
  /** Build the mirrored output batch's pending/blocked entries. */
  'build',
  /** Re-queue its failed entries. */
  'retry-failed',
  /** Rebuild its already-`ready` entries in place. */
  'rebuild',
] as const;
export type BatchActionId = (typeof BATCH_ACTIONS)[number];

/**
 * Which build scope each of the three build actions sends. The ids above are the
 * OPERATOR's vocabulary ("retry failed", "rebuild"); `pending`/`failed`/`terminal` are
 * the WIRE's (see `buildScopeSchema`). Keeping the mapping here is what stops a menu
 * from hardcoding a scope string, and it is also the reason-pairing boundary: `build`
 * maps to `pending`, which must be sent WITHOUT a reason, while the other two require
 * one (`buildScopeNeedsReason`).
 */
export const BATCH_BUILD_SCOPE = {
  build: 'pending',
  'retry-failed': 'failed',
  rebuild: 'terminal',
} as const satisfies Record<BatchActionId, BuildScope>;

/**
 * How long the source-path catalog (`GET /metadata/sources`) stays fresh. It is a
 * compiled-in vocabulary on the backend — a path appears or disappears only when
 * the platform ships a new field — so an hour avoids refetching it every time the
 * binding editor opens, while still picking up a deploy within one session.
 */
export const METADATA_SOURCES_STALE_TIME = 60 * 60_000;

/**
 * Poll interval (ms) for output summary / batch / entry reads while any state
 * is still `pending` or `building`, so async build progress surfaces without a
 * manual refresh. Polling stops once every tracked state is terminal.
 */
export const OUTPUT_BUILD_POLL_MS = 4_000;

/** Backoff intervals after a build has remained active for 30 seconds / two minutes. */
export const OUTPUT_BUILD_POLL_STEADY_MS = 8_000;
export const OUTPUT_BUILD_POLL_SLOW_MS = 15_000;

/**
 * A customer-metadata string field whose schema `maxLength` exceeds this renders
 * as a multiline textarea (label above) instead of a single-line input in the
 * schema-driven metadata edit form (see `lib/metadataForm`). Mirrors the
 * asset-metadata threshold.
 */
export const METADATA_MULTILINE_MAXLENGTH_THRESHOLD = 256;

/**
 * Field styling for the customer-metadata form, mirroring the asset detail
 * metadata panel (`features/asset-metadata`) so the two metadata surfaces read as
 * one system: a dense property row (label left, value right) separated by
 * hairline dividers, and a value control that is chrome-free apart from a 2px
 * left-border rail which fills in on focus.
 *
 * Unlike the asset panel, this form is submit-based (a PATCH merge behind a Save
 * button) rather than per-field autosave, so the control stays a normal
 * always-editable input instead of the asset panel's click-to-edit toggle — the
 * padding box, type scale, and focus rail are kept identical.
 */
export const METADATA_FIELD_ROWS_CLASS_NAME = 'divide-y divide-border/50';
export const METADATA_FIELD_LABEL_CLASS_NAME =
  'w-32 shrink-0 text-balance text-muted-foreground text-xs leading-tight';
/** Shared by the text/number input and the boolean select (`px-3` lines their values up with the inline Combobox trigger). */
export const METADATA_FIELD_CONTROL_CLASS_NAME =
  'h-auto rounded-none border-0 border-l-2 border-l-transparent bg-transparent px-3 py-0.5 text-xs shadow-none transition-colors hover:bg-muted/60 focus-visible:border-l-primary focus-visible:bg-muted/50 focus-visible:ring-0 aria-invalid:border-l-destructive';
/** Aligns the error line under the value column (label width + row gap). */
export const METADATA_FIELD_ERROR_CLASS_NAME = 'mt-1 pl-[8.5rem] text-destructive text-xs';

/**
 * The delivery status strip's complete build-state partition. `readyDuration` is the
 * only member dropped in compact mode.
 */
const DELIVERY_STATUS_READOUTS = [
  'batches',
  'ready',
  'building',
  'pending',
  'failed',
  'deleted',
  'readyDuration',
] as const;
export type DeliveryStatusReadoutKey = (typeof DELIVERY_STATUS_READOUTS)[number];

/**
 * The output status readouts, in display order — the three the batch workspace's
 * output line needs to state the complete build partition. The entry total is stated
 * inside `ready` (`14/20`), and only ready media duration is shown.
 *
 * `failed` and `blocked` are the two facts that need attention, and this line is the
 * **one** surface over them — a non-zero count is emphasised by its readout's tone
 * (see `lib/outputStatusStrip.ts`) rather than restated in a banner and drawer
 * beneath, which is what these counts used to feed.
 */
const OUTPUT_STATUS_READOUTS = [
  'ready',
  'building',
  'pending',
  'failed',
  'blocked',
  'readyDuration',
  'metadataOk',
  'metadataWarning',
  'metadataIncomplete',
  'metadataNotEvaluated',
  'finalizationState',
] as const;
export type OutputStatusReadoutKey = (typeof OUTPUT_STATUS_READOUTS)[number];
